import React, { useEffect, useRef, useState } from "react";
import {
  Button, Checkbox, Dialog, DialogContent, DialogContentText, DialogTitle, FormControlLabel, IconButton,
  MenuItem, TextField,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import {
  availabilitySummary, boatNames, cleanDescription, contactName, formatWhen, groupedBoats, sortMessages,
  subjectBookingId, subjectEmail, subjectLeadId, subjectName, subjectPhone, subjectReference,
} from "./model";
import {
  Compose, ComposeHead, ContextList, ConversationPane, DialogFooter, FooterNote, InfoPanel,
  MessageCard, PrimaryButton, SecondaryButton, SourceList, SourceRow, TextButton, Transcript,
} from "./styles";

async function runSaved({ setPending, setError, request, onAction, onNotify, success, onSuccess, onClose }) {
  setPending(true);
  setError("");
  try {
    const result = await onAction(request);
    if (onSuccess) onSuccess(result);
    else {
      onNotify(success || "Saved");
      if (result?.refreshRequired) onNotify("Saved. Refresh the page to see the latest details.");
      onClose();
    }
  } catch (error) {
    setError(error.message || "This action could not be completed. Your changes are still here.");
    setPending(false);
  }
}

function DialogFrame({ title, description, wide, pending, error, note, discard, onDiscard, onClose, children, actions }) {
  return (
    <Dialog open fullWidth maxWidth={wide ? "md" : "sm"} onClose={() => { if (!pending) onClose(); }}>
      <DialogTitle>
        {title}
        <IconButton aria-label="Close dialog" disabled={pending} onClick={onClose} style={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {description ? <DialogContentText style={{ margin: "0 24px 8px" }}>{description}</DialogContentText> : null}
      <DialogContent>{children}</DialogContent>
      <DialogFooter>
        <FooterNote className={error ? "alert" : ""} role={error ? "alert" : undefined}>
          {discard ? <>Unsaved changes. <Button color="primary" onClick={onDiscard}>Discard changes</Button></> : (error || note)}
        </FooterNote>
        <div>{actions}</div>
      </DialogFooter>
    </Dialog>
  );
}

function useConversation(record, onLoadConversation) {
  const [state, setState] = useState({ status: "loading", messages: [], error: "" });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setState({ status: "loading", messages: [], error: "" });
    onLoadConversation({ bookingId: subjectBookingId(record), leadId: subjectLeadId(record) })
      .then((messages) => { if (active) setState({ status: "ready", messages, error: "" }); })
      .catch((error) => { if (active) setState({ status: "error", messages: [], error: error.message }); });
    return () => { active = false; };
  }, [record, attempt, onLoadConversation]);
  return { ...state, retry: () => setAttempt((value) => value + 1) };
}

function ConversationView({ record, messages, limit, onAction, onNotify, onShowAll }) {
  const [rows, setRows] = useState(() => sortMessages(messages));
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => setRows(sortMessages(messages)), [messages]);
  const unreadIds = [...new Set(rows.filter((message) => (message.lead || message.owner) && !message.seenOn).map((message) => message.id))];
  const canMark = Boolean(subjectBookingId(record) && subjectLeadId(record) && unreadIds.length);
  const visible = limit ? rows.slice(-limit) : rows;

  const markRead = async () => {
    if (marking) return;
    setMarking(true);
    setError("");
    try {
      const result = await onAction({
        action: "mark-messages-read",
        bookingId: subjectBookingId(record),
        leadId: subjectLeadId(record),
        input: { messageIds: unreadIds },
      });
      if (!Array.isArray(result?.messages) || !unreadIds.every((id) => result.messages.some((message) => message.id === id && message.seenOn))) {
        throw new Error("Messages could not be confirmed as read. Please refresh the conversation.");
      }
      const confirmed = new Map(result.messages.map((message) => [message.id, message.seenOn]));
      setRows((current) => current.map((message) => (confirmed.has(message.id) ? { ...message, seenOn: confirmed.get(message.id) } : message)));
      onNotify(result.refreshRequired ? "Messages marked as read. Refresh the list to see the latest counts." : "Messages marked as read");
    } catch (actionError) {
      setError(actionError.message);
    } finally {
      setMarking(false);
    }
  };

  return (
    <div>
      {limit && rows.length > limit ? <p>Showing the latest {limit} of {rows.length} messages.</p> : null}
      {canMark ? <SecondaryButton disabled={marking} onClick={markRead}>{marking ? "Marking as read..." : "Mark all as read"}</SecondaryButton> : null}
      {visible.length ? visible.map((message) => {
        const body = message.content ? cleanDescription(message.content) : "";
        return (
          <MessageCard key={message.id}>
            <MessageDetails message={message} />
            <p>{body.trim() ? body : "Message text is unavailable."}</p>
          </MessageCard>
        );
      }) : <p>No saved messages for this contact.</p>}
      {onShowAll ? <TextButton onClick={() => onShowAll(rows)}>View full conversation</TextButton> : null}
      {error ? <FooterNote className="alert" role="alert">{error}</FooterNote> : null}
    </div>
  );
}

function MessageDetails({ message }) {
  const sender = message.lead || message.owner;
  const recipient = message.toLead || message.toOwner;
  const direction = sender ? `From ${contactName(sender) || "Sender unavailable"}`
    : recipient ? `To ${contactName(recipient) || "Recipient unavailable"}`
      : message.toAddress ? `To ${message.toAddress}` : "Message";
  const associations = [
    message.booking?.reference && `Booking ${message.booking.reference}`,
    message.quote?.reference && `Quote ${message.quote.reference}`,
    message.bookingOption?.reference && `Options ${message.bookingOption.reference}`,
  ].filter(Boolean).join(" · ");
  const status = { sent: "Sent", recieved: "Received", pending: "Pending", error: "Not sent" }[message.status];
  const read = sender ? (message.seenOn ? "Read" : "Unread") : "";
  const detail = [formatWhen(message.createdAt, true), associations, status, read].filter(Boolean).join(" · ");
  return (
    <>
      <h3>{direction}</h3>
      <div style={{ color: "#777", fontSize: 12 }}>{detail}</div>
    </>
  );
}

function NoteDialog({ record, onAction, onNotify, onClose }) {
  const [notes, setNotes] = useState(record.notes || "");
  const [dirty, setDirty] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [discard, setDiscard] = useState(false);
  const requestClose = () => {
    if (pending) return;
    if (dirty) { setDiscard(true); return; }
    onClose();
  };
  return (
    <DialogFrame
      title="Booking notes"
      description={`${subjectName(record) || "Name unavailable"} · ${subjectReference(record)}`}
      pending={pending}
      error={error}
      discard={discard}
      onDiscard={onClose}
      onClose={requestClose}
      actions={(
        <>
          <SecondaryButton disabled={pending} onClick={requestClose}>Cancel</SecondaryButton>
          <PrimaryButton disabled={pending} onClick={() => runSaved({
            setPending, setError, onAction, onNotify, onClose, success: "Notes saved",
            request: { action: "save-notes", bookingId: subjectBookingId(record), leadId: subjectLeadId(record), input: { notes } },
          })}
          >{pending ? "Saving..." : "Save note"}</PrimaryButton>
        </>
      )}
    >
      <TextField
        id="editNote"
        label="Notes"
        value={notes}
        multiline
        rows={6}
        fullWidth
        variant="outlined"
        autoFocus
        disabled={pending}
        onChange={(event) => { setNotes(event.target.value); setDirty(true); setDiscard(false); }}
      />
    </DialogFrame>
  );
}

function MessageDialog({ record, taskId, initialBody, onAction, onLoadConversation, onNotify, onSent, onClose }) {
  const [body, setBody] = useState(initialBody || "");
  const [sms, setSms] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [pending, setPending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [discard, setDiscard] = useState(false);
  const [review, setReview] = useState(null);
  const [transcript, setTranscript] = useState(false);
  const [suggestionReady, setSuggestionReady] = useState(false);
  const bodyRef = useRef(body);
  const session = useRef(null);
  bodyRef.current = body;
  const conversation = useConversation(record, onLoadConversation);

  const suggest = async () => {
    if (suggesting || pending) return;
    const original = bodyRef.current;
    const requestId = crypto.randomUUID();
    session.current = {
      original,
      applied: false,
      reply: "",
      promise: onAction({
        action: "suggest-reply",
        bookingId: subjectBookingId(record),
        leadId: subjectLeadId(record),
        requestId,
        input: { draft: original },
      }),
    };
    setSuggesting(true);
    setError("");
    setNote("Preparing your message...");
    setSuggestionReady(false);
    try {
      const result = await session.current.promise;
      if (!result?.reply?.trim()) throw new Error("A reply could not be suggested. Your message is still here.");
      session.current.reply = result.reply;
      if (bodyRef.current === original) {
        setBody(result.reply);
        session.current.applied = true;
        setDirty(true);
        setNote("");
      } else {
        setSuggestionReady(true);
        setNote("");
      }
    } catch (actionError) {
      setError(actionError.message);
      setNote("");
    } finally {
      setSuggesting(false);
    }
  };

  const requestClose = () => {
    if (pending) return;
    if (transcript) { setTranscript(false); return; }
    if (review) { setReview(null); return; }
    if (dirty) { setDiscard(true); return; }
    onClose();
  };

  const openReview = () => {
    if (!body.trim()) { setError("Write a message before reviewing it."); return; }
    if (!subjectEmail(record)) { setError("An email address is needed to send this message."); return; }
    if (sms && !subjectPhone(record)) { setError("A phone number is needed for an SMS notification."); return; }
    setError("");
    setDirty(true);
    setReview({ body, sms });
  };

  const send = () => runSaved({
    setPending, setError, onAction, onNotify, onClose, success: "Message sent",
    request: {
      action: "send-message",
      bookingId: subjectBookingId(record),
      leadId: subjectLeadId(record),
      taskId,
      input: { content: review.body, sendSms: review.sms },
    },
    onSuccess: (result) => onSent(record, result),
  });

  const title = transcript ? "Conversation" : review ? "Review message" : "Quick message";
  const description = subjectName(record) || subjectReference(record);
  return (
    <DialogFrame
      title={title}
      description={description}
      wide
      pending={pending}
      error={error}
      note={note}
      discard={!review && !transcript && discard}
      onDiscard={onClose}
      onClose={requestClose}
      actions={transcript ? (
        <SecondaryButton onClick={() => setTranscript(false)}>Back to message</SecondaryButton>
      ) : review ? (
        <>
          <SecondaryButton disabled={pending} onClick={() => setReview(null)}>Back</SecondaryButton>
          <PrimaryButton disabled={pending} onClick={send}>{pending ? "Sending message..." : "Send message"}</PrimaryButton>
        </>
      ) : (
        <>
          <SecondaryButton disabled={pending} onClick={requestClose}>Close</SecondaryButton>
          <PrimaryButton disabled={pending} onClick={openReview}>Review message</PrimaryButton>
        </>
      )}
    >
      {transcript ? (
        conversation.status === "ready" ? <ConversationView record={record} messages={conversation.messages} onAction={onAction} onNotify={onNotify} /> : <p>{conversation.error || "Loading conversation..."}</p>
      ) : review ? (
        <>
          <InfoPanel>{`To: ${subjectEmail(record)}\n${review.sms ? `SMS notification: ${subjectPhone(record)}` : "SMS notification: off"}`}</InfoPanel>
          <InfoPanel>{review.body}</InfoPanel>
        </>
      ) : (
        <Compose>
          <ConversationPane>
            <h3>Conversation</h3>
            <Transcript>
              {conversation.status === "loading" ? <p>Loading conversation...</p> : null}
              {conversation.status === "error" ? (
                <p>{conversation.error} <TextButton onClick={conversation.retry}>Retry</TextButton></p>
              ) : null}
              {conversation.status === "ready" ? (
                <ConversationView
                  record={record}
                  messages={conversation.messages}
                  limit={10}
                  onAction={onAction}
                  onNotify={onNotify}
                  onShowAll={() => setTranscript(true)}
                />
              ) : null}
            </Transcript>
            <ContextList>
              <div><dt>Cruise</dt><dd>{formatWhen(record.cruiseDate, true)}</dd></div>
              <div><dt>Guests / hours</dt><dd>{record.groupSize ?? "Unavailable"} / {record.hours ?? "Unavailable"}</dd></div>
              <div><dt>Boats</dt><dd>{boatNames(record) || "None"}</dd></div>
            </ContextList>
          </ConversationPane>
          <section>
            <ComposeHead>
              <span>Message</span>
              <SecondaryButton disabled={suggesting || pending} onClick={suggest}>{suggesting ? "Preparing..." : "Suggest reply"}</SecondaryButton>
            </ComposeHead>
            <TextField
              value={body}
              multiline
              rows={8}
              fullWidth
              variant="outlined"
              autoFocus
              placeholder="Write your message"
              disabled={pending}
              onChange={(event) => { setBody(event.target.value); setDirty(true); setDiscard(false); }}
            />
            <FormControlLabel
              control={<Checkbox color="primary" checked={sms} disabled={pending} onChange={(event) => { setSms(event.target.checked); setDirty(true); }} />}
              label="Send SMS notification"
            />
            <p>To: {subjectEmail(record) || "Email unavailable"}</p>
            {suggestionReady ? <TextButton onClick={() => { setBody(session.current.reply); session.current.applied = true; setDirty(true); setSuggestionReady(false); }}>Use suggestion</TextButton> : null}
          </section>
        </Compose>
      )}
    </DialogFrame>
  );
}

function ConversationDialog({ record, onAction, onLoadConversation, onNotify, onClose, backLabel }) {
  const conversation = useConversation(record, onLoadConversation);
  return (
    <DialogFrame
      title="Conversation"
      description={subjectName(record) || subjectReference(record) || record.id || "Enquiry"}
      wide
      onClose={onClose}
      actions={<SecondaryButton onClick={onClose}>{backLabel || "Close"}</SecondaryButton>}
    >
      {conversation.status === "loading" ? <p>Loading conversation...</p> : null}
      {conversation.status === "error" ? <p>{conversation.error} <SecondaryButton onClick={conversation.retry}>Retry</SecondaryButton></p> : null}
      {conversation.status === "ready" ? <ConversationView record={record} messages={conversation.messages} onAction={onAction} onNotify={onNotify} /> : null}
    </DialogFrame>
  );
}

function AvailabilityDialog({ record, onAction, onClose }) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const boatList = groupedBoats(record).map((boat) => boat.name).join("\n");
  const [results, setResults] = useState(boatList || "No boats added.");
  return (
    <DialogFrame
      title="Boat availability"
      description={`${subjectName(record) || "Name unavailable"} · ${subjectReference(record)}`}
      pending={pending}
      error={error}
      note={note}
      onClose={onClose}
      actions={(
        <>
          <SecondaryButton disabled={pending} onClick={onClose}>Close</SecondaryButton>
          <PrimaryButton disabled={pending} onClick={() => {
            if (!groupedBoats(record).length) { setError("Add a boat before checking availability."); return; }
            runSaved({
              setPending, setError, onAction, onNotify: () => {}, onClose, success: "Checked",
              request: { action: "check-availability", bookingId: subjectBookingId(record), leadId: subjectLeadId(record), input: {} },
              onSuccess: (result) => {
                setResults((result.checks || []).map((check) => `${check.boatName}: ${availabilitySummary(check)}`).join("\n"));
                setNote(`Checked ${formatWhen(result.checkedAt, true)}`);
                setPending(false);
              },
            });
          }}
          >{pending ? "Checking availability..." : "Check availability"}</PrimaryButton>
        </>
      )}
    >
      <p>Check availability for all boats in this booking.</p>
      <InfoPanel>{results}</InfoPanel>
    </DialogFrame>
  );
}

function AvailabilityRequestDialog({ record, onAction, onNotify, onClose }) {
  const [message, setMessage] = useState("");
  const [dirty, setDirty] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [discard, setDiscard] = useState(false);
  const names = groupedBoats(record).map((boat) => boat.name).join("\n");
  const requestClose = () => {
    if (pending) return;
    if (dirty) { setDiscard(true); return; }
    onClose();
  };
  return (
    <DialogFrame
      title="Send availability check"
      description={`${subjectName(record) || "Name unavailable"} · ${subjectReference(record)}`}
      wide
      pending={pending}
      error={error}
      discard={discard}
      onDiscard={onClose}
      onClose={requestClose}
      actions={(
        <>
          <SecondaryButton disabled={pending} onClick={requestClose}>Cancel</SecondaryButton>
          <PrimaryButton disabled={pending} onClick={() => {
            if (!names) { setError("Add a boat before sending a request."); return; }
            if (!message.trim()) { setError("Write a message before sending the request."); return; }
            runSaved({
              setPending, setError, onAction, onNotify, onClose, success: "Availability requests sent",
              request: { action: "send-availability-request", bookingId: subjectBookingId(record), leadId: subjectLeadId(record), input: { message } },
            });
          }}
          >{pending ? "Sending requests..." : "Send request"}</PrimaryButton>
        </>
      )}
    >
      <p>Send an availability request to the operators for all boats in this booking.</p>
      <InfoPanel>{names}</InfoPanel>
      <TextField
        label="Message to the operators"
        value={message}
        multiline
        rows={5}
        fullWidth
        variant="outlined"
        autoFocus
        disabled={pending}
        style={{ marginTop: 12 }}
        onChange={(event) => { setMessage(event.target.value); setDirty(true); setDiscard(false); }}
      />
    </DialogFrame>
  );
}

function TaskDialog({ record, task, onAction, onNotify, onClose }) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  return (
    <DialogFrame
      title="Complete task"
      description={subjectName(record) || subjectReference(record)}
      pending={pending}
      error={error}
      onClose={() => { if (!pending) onClose(); }}
      actions={(
        <>
          <SecondaryButton disabled={pending} onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton disabled={pending} onClick={() => runSaved({
            setPending, setError, onAction, onNotify, onClose, success: "Task completed",
            request: { action: "complete-task", bookingId: subjectBookingId(record), leadId: subjectLeadId(record), taskId: task.id, input: {} },
          })}
          >{pending ? "Saving..." : "Mark as done"}</PrimaryButton>
        </>
      )}
    >
      <p>{task.name}</p>
    </DialogFrame>
  );
}

function BookingAvailabilityDialog({ record, onAction, onNotify, onClose }) {
  const [value, setValue] = useState(record.available === true ? "yes" : record.available === false ? "no" : "");
  const [dirty, setDirty] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [discard, setDiscard] = useState(false);
  const requestClose = () => {
    if (pending) return;
    if (dirty) { setDiscard(true); return; }
    onClose();
  };
  return (
    <DialogFrame
      title="Booking availability"
      description={subjectName(record) || subjectReference(record)}
      pending={pending}
      error={error}
      discard={discard}
      onDiscard={onClose}
      onClose={requestClose}
      actions={(
        <>
          <SecondaryButton disabled={pending} onClick={requestClose}>Cancel</SecondaryButton>
          <PrimaryButton disabled={pending} onClick={() => {
            if (!value) { setError("Choose availability."); return; }
            runSaved({
              setPending, setError, onAction, onNotify, onClose, success: "Availability saved",
              request: { action: "set-booking-availability", bookingId: subjectBookingId(record), leadId: subjectLeadId(record), input: { available: value === "yes" } },
            });
          }}
          >{pending ? "Saving..." : "Save"}</PrimaryButton>
        </>
      )}
    >
      <TextField
        select
        fullWidth
        label="Manual booking availability"
        value={value}
        variant="outlined"
        disabled={pending}
        onChange={(event) => { setValue(event.target.value); setDirty(true); setDiscard(false); }}
      >
        <MenuItem value="">Choose availability</MenuItem>
        <MenuItem value="yes">Available</MenuItem>
        <MenuItem value="no">Unavailable</MenuItem>
      </TextField>
    </DialogFrame>
  );
}

function EnquiryDialog({ tasks, offset, onOpenConversation, onOpenEnquiries, onClose }) {
  const page = tasks.slice(offset, offset + 20);
  return (
    <DialogFrame
      title="New enquiries"
      description={`${tasks.length} pending enquiry tasks`}
      wide
      onClose={onClose}
      actions={(
        <>
          <SecondaryButton onClick={onClose}>Close</SecondaryButton>
          {offset > 0 ? <SecondaryButton onClick={() => onOpenConversation(null, Math.max(0, offset - 20))}>Previous</SecondaryButton> : null}
          {offset + 20 < tasks.length ? <SecondaryButton onClick={() => onOpenConversation(null, offset + 20)}>Next 20</SecondaryButton> : null}
          <PrimaryButton onClick={onOpenEnquiries}>Open enquiries</PrimaryButton>
        </>
      )}
    >
      {page.length ? (
        <SourceList>
          {page.map((entry) => (
            <SourceRow key={entry.id}>
              <h3>{entry.name || "Name unavailable"}</h3>
              <div style={{ color: "#777", fontSize: 12 }}>{entry.email || "Email unavailable"} · {formatWhen(entry.created, true)}</div>
              <p>{entry.task?.name || "Enquiry follow-up"}</p>
              <p>{cleanDescription(entry.task?.description)}</p>
              {entry.leadId ? <TextButton onClick={() => onOpenConversation(entry)}>View conversation</TextButton> : null}
            </SourceRow>
          ))}
        </SourceList>
      ) : <p>No pending enquiry tasks.</p>}
    </DialogFrame>
  );
}

function SentDialog({ name, message, onClose }) {
  return (
    <DialogFrame title="Message sent" description={name} onClose={onClose} actions={<SecondaryButton onClick={onClose}>Close</SecondaryButton>}>
      <p>{message}</p>
    </DialogFrame>
  );
}

export default function DashboardDialogs({ dialog, onAction, onLoadConversation, onNotify, onNavigate, onChange, onClose, onSent }) {
  if (!dialog) return null;
  if (dialog.type === "note") return <NoteDialog record={dialog.record} onAction={onAction} onNotify={onNotify} onClose={onClose} />;
  if (dialog.type === "message") {
    return (
      <MessageDialog
        record={dialog.record}
        taskId={dialog.taskId}
        initialBody={dialog.initialBody}
        onAction={onAction}
        onLoadConversation={onLoadConversation}
        onNotify={onNotify}
        onSent={onSent}
        onClose={onClose}
      />
    );
  }
  if (dialog.type === "conversation") {
    return (
      <ConversationDialog
        record={dialog.record}
        onAction={onAction}
        onLoadConversation={onLoadConversation}
        onNotify={onNotify}
        backLabel={dialog.returnTo ? "Back" : "Close"}
        onClose={() => (dialog.returnTo ? onChange(dialog.returnTo) : onClose())}
      />
    );
  }
  if (dialog.type === "availability") return <AvailabilityDialog record={dialog.record} onAction={onAction} onClose={onClose} />;
  if (dialog.type === "availability-request") return <AvailabilityRequestDialog record={dialog.record} onAction={onAction} onNotify={onNotify} onClose={onClose} />;
  if (dialog.type === "task-done") return <TaskDialog record={dialog.record} task={dialog.task} onAction={onAction} onNotify={onNotify} onClose={onClose} />;
  if (dialog.type === "booking-availability") return <BookingAvailabilityDialog record={dialog.record} onAction={onAction} onNotify={onNotify} onClose={onClose} />;
  if (dialog.type === "enquiries") {
    return (
      <EnquiryDialog
        tasks={dialog.tasks}
        offset={dialog.offset || 0}
        onClose={onClose}
        onOpenEnquiries={() => {
          try { onNavigate({ action: "enquiries" }); }
          catch (error) { onNotify(error.message); }
        }}
        onOpenConversation={(entry, nextOffset) => {
          if (!entry) onChange({ ...dialog, offset: nextOffset });
          else onChange({ type: "conversation", record: entry, returnTo: dialog });
        }}
      />
    );
  }
  if (dialog.type === "sent") return <SentDialog name={dialog.name} message={dialog.message} onClose={onClose} />;
  return null;
}
